import { StanPill } from '@/components';
import { StanCalendar } from '@/components/core-components/StanCalendar/StanCalendar';
import {
    StanFloatingMenu,
    type StanFloatingMenuHandle,
} from '@/components/core-components/StanDropdown/FloatingMenu/StanFloatingMenu';
import { useCallback, useRef, useState } from 'react';
import type { DateRange } from 'react-day-picker';
import type { AnalyticsDateRangePickerProps } from '../types/analytics.types';
import type { DateRangeType } from '@/lib/constants/dateFormats';
import { CHART_CONFIG, RANGE_TYPES } from '../constants/analytics.constants';
import { getCustomRangeText, getMTDText } from '../logic/dateRange.logic';
import { calculateDateRange } from '@/lib/utils/dateRange';
/**
 * Calculate the maximum selectable date (current date + offset)
 */
const getMaxDate = () => {
    const date = new Date();
    date.setDate(date.getDate() + CHART_CONFIG.MAX_DATE_OFFSET);
    return date.getTime();
};

export function AnalyticsDateRangePicker({ onRangeSelected }: AnalyticsDateRangePickerProps) {
    const [rangeType, setRangeType] = useState<DateRangeType>(RANGE_TYPES.LAST_14_DAYS);
    const [startDate, setStartDate] = useState<Date>(new Date(new Date().setDate(new Date().getDate() - 14)));
    const [endDate, setEndDate] = useState<Date>(new Date());
    const calendarMenuRef = useRef<StanFloatingMenuHandle>(null);

    /**
     * Handles date range selection with proper state updates
     * Consolidates state changes to prevent unnecessary re-renders
     */
    const handleRangeSelect = useCallback(
        (type: DateRangeType) => {
            const dateRange = calculateDateRange(type);

            if (dateRange?.from && dateRange?.to) {
                // Batch state updates to prevent multiple re-renders
                setRangeType(type);
                setStartDate(dateRange.from);
                setEndDate(dateRange.to);

                // Call parent callback after state updates
                onRangeSelected({
                    startDate: dateRange.from,
                    endDate: dateRange.to,
                    rangeType: type,
                });
            }
        },
        [onRangeSelected]
    );

    /**
     * Handles date range selection from the calendar picker.
     * Updates state when a complete range is selected.
     * For same-day selections, sets start to 00:00:00 and end to 23:59:59.
     */
    const handleCalendarSelect = (range: DateRange | undefined) => {
        if (range?.from && range?.to) {
            let adjustedStartDate = range.from;
            let adjustedEndDate = range.to;

            // Start date should always be the start of the day, and end date the end of the day
            // Set start to 00:00:00
            adjustedStartDate = new Date(range.from);
            adjustedStartDate.setHours(0, 0, 0, 0);

            // Set end to 23:59:59.999
            adjustedEndDate = new Date(range.to);
            adjustedEndDate.setHours(23, 59, 59, 0);

            setStartDate(adjustedStartDate);
            setEndDate(adjustedEndDate);
            setRangeType(RANGE_TYPES.CUSTOM);
            onRangeSelected({
                startDate: adjustedStartDate,
                endDate: adjustedEndDate,
                rangeType: RANGE_TYPES.CUSTOM,
            });
            calendarMenuRef.current?.setMenu(false);
        }
    };

    /**
     * Handles the Month-To-Date button click.
     * Sets the date range from the start of the current month to tomorrow.
     */

    return (
        <div className='mb-6 flex flex-wrap items-center gap-4'>
            <StanPill
                name={RANGE_TYPES.LAST_7_DAYS}
                active={rangeType === RANGE_TYPES.LAST_7_DAYS}
                onClick={() => handleRangeSelect(RANGE_TYPES.LAST_7_DAYS)}
            >
                <span className='hidden md:inline'>Last 7 Days</span>
                <span className='inline md:hidden'>7D</span>
            </StanPill>

            <StanPill
                name={RANGE_TYPES.LAST_14_DAYS}
                active={rangeType === RANGE_TYPES.LAST_14_DAYS}
                onClick={() => handleRangeSelect(RANGE_TYPES.LAST_14_DAYS)}
            >
                <span className='hidden md:inline'>Last 14 Days</span>
                <span className='inline md:hidden'>14D</span>
            </StanPill>

            <StanPill
                name={RANGE_TYPES.MONTH_TO_DATE}
                active={rangeType === RANGE_TYPES.MONTH_TO_DATE}
                onClick={() => handleRangeSelect(RANGE_TYPES.MONTH_TO_DATE)}
            >
                {getMTDText()}
            </StanPill>

            <StanFloatingMenu
                ref={calendarMenuRef}
                name={RANGE_TYPES.CUSTOM}
                trigger={
                    <StanPill name={RANGE_TYPES.CUSTOM} active={rangeType === RANGE_TYPES.CUSTOM}>
                        {getCustomRangeText(rangeType, startDate, endDate)}
                    </StanPill>
                }
                className='!max-h-fit'
            >
                <StanCalendar
                    analyticsName='analytics-date-range-picker'
                    mode='range'
                    selected={{ from: startDate, to: endDate }}
                    onSelect={handleCalendarSelect}
                    max={getMaxDate()}
                    disabled={{ after: new Date() }}
                />
            </StanFloatingMenu>
        </div>
    );
}
